/**
 * Risk #4 hit panel — CANON: fill RiskHitPanelLayout slots only.
 * Do not add a custom card shell. See `./riskCardLayoutCanon.js`.
 */
import { Link } from 'react-router-dom';
import { resolveSupportActionLabel } from '../constants/supportActionLabels';
import { buildDeviceHistoryPath } from '../pages/deviceHistoryPath';
import { formatDateTime } from './RiskCard';
import Risk4ForecastFeature from './Risk4ForecastFeature';
import RiskHitPanelLayout, {
  RISK_HIT_LAYOUT,
  RiskHitMetricGrid,
} from './RiskHitPanelLayout';

const METRIC_HINTS = {
  quality: 'Насколько можно доверять этой оценке по имеющимся данным.',
  level: 'Текущее напряжение батареи. Ниже 3,0 В — критично.',
  sag: 'Насколько напряжение просело относительно обычного уровня этого прибора.',
  rul: 'Срок до отказа без калиброванной модели не считаем.',
};

const QUALITY_STATUS_RU = {
  good: 'хорошая',
  limited: 'средняя',
  poor: 'низкая',
  bad: 'низкая',
  unknown: 'неизвестно',
};

function formatMv(value) {
  return value == null || !Number.isFinite(Number(value)) ? 'нет данных' : `${Number(value)} мВ`;
}

/** Ordinary-person voltage: 3488 мВ → 3,49 В */
function formatVolts(value) {
  if (value == null || !Number.isFinite(Number(value))) return 'нет данных';
  return `${(Number(value) / 1000).toFixed(2).replace('.', ',')} В`;
}

function formatScore(score) {
  if (score == null || !Number.isFinite(Number(score))) return 'н/д';
  return String(Number(score));
}

function formatQualityPlain(value, status) {
  if (status && QUALITY_STATUS_RU[status]) return QUALITY_STATUS_RU[status];
  if (value == null || !Number.isFinite(Number(value))) return 'нет данных';
  const n = Number(value);
  if (n >= 70) return 'хорошая';
  if (n >= 40) return 'средняя';
  return 'низкая';
}

function formatSagPlain(features) {
  if (features.sag_mv == null || !Number.isFinite(Number(features.sag_mv))) return 'нет данных';
  const mv = Number(features.sag_mv);
  if (mv <= 50) return 'небольшая';
  if (mv <= 150) return `${mv} мВ`;
  return `${mv} мВ · заметная`;
}


/** Risk #4 — content only; shell is RiskHitPanelLayout. */
export default function Risk4HitPanel({ hit, onOpenHistory }) {
  const { facts, features, rule_result: ruleResult, lower_envelope_projection: envelope } = hit;
  const score = ruleResult.risk_score;
  const numericScore = score == null || !Number.isFinite(Number(score)) ? null : Number(score);
  const riskLevel = ruleResult.risk_level || 'unknown';
  const serial = facts.serial_number || 'Без serial';
  const deviceId = facts.device_id;
  const criticalRows = [
    { key: 'action', label: 'Что сделать', value: resolveSupportActionLabel(ruleResult) },
    {
      key: 'level',
      label: 'Напряжение батареи',
      hint: METRIC_HINTS.level,
      value: formatVolts(features.level_mv),
      numeric: true,
    },
    {
      key: 'sag',
      label: 'Просадка',
      hint: METRIC_HINTS.sag,
      value: formatSagPlain(features),
    },
    {
      key: 'quality',
      label: 'Надёжность оценки',
      hint: METRIC_HINTS.quality,
      value: formatQualityPlain(ruleResult.risk_quality, ruleResult.risk_quality_status),
    },
  ];

  const detailRows = [
    {
      key: 'level-mv',
      label: 'Напряжение (мВ)',
      value: formatMv(features.level_mv),
      numeric: true,
    },
    {
      key: 'sag-detail',
      label: 'Просадка подробно',
      value:
        features.sag_mv == null
          ? 'нет данных'
          : `${features.sag_mv} мВ` +
            (features.sag_pct_voltage != null ? ` · ${features.sag_pct_voltage}%` : '') +
            (features.baseline_kind ? ` · ${features.baseline_kind}` : ''),
    },
    {
      key: 'quality-score',
      label: 'Оценка качества',
      value:
        ruleResult.risk_quality == null
          ? 'нет данных'
          : `${Number(ruleResult.risk_quality)}/100`,
      numeric: true,
    },
    {
      key: 'tail',
      label: 'Нижний уровень (p10)',
      value: formatMv(features.tail_floor_mv),
      numeric: true,
    },
    {
      key: 'last-packet',
      label: 'Последнее напряжение',
      value: formatMv(features.last_sample_voltage_mv ?? features.last_voltage_mv),
      numeric: true,
    },
    {
      key: 'daily-min',
      label: 'Минимум за день',
      value: formatMv(features.last_daily_min_mv),
      numeric: true,
    },
    {
      key: 'age',
      label: 'Возраст батареи',
      value:
        features.age_status === 'known' && features.battery_age_days != null
          ? `${features.battery_age_days} дн.`
          : `неизвестен · есть история ${features.observed_history_days ?? 0} дн.`,
    },
    {
      key: 'data-quality',
      label: 'Качество данных',
      value: formatQualityPlain(features.data_quality, features.data_quality_status),
    },
    {
      key: 'regime',
      label: 'Режим измерения',
      value: features.measurement_regime_id
        ? `${features.measurement_regime_id}${features.measurement_regime_changed ? ' · смена' : ''}`
        : 'неизвестно',
    },
    {
      key: 'rul',
      label: 'До отказа',
      hint: METRIC_HINTS.rul,
      value:
        features.rul_status === 'unavailable' || features.rul_days == null
          ? 'не считаем'
          : `${features.rul_days} дн.`,
    },
    {
      key: 'last-at',
      label: 'Последний замер',
      value: formatDateTime(features.last_sample_at || features.last_voltage_at),
    },
  ];

  return (
    <RiskHitPanelLayout
      embedded
      riskNumber={4}
      badges={
        <>
          <span className="inline-flex shrink-0 rounded-full border border-slate-300 dark:border-slate-600/50 bg-slate-100 dark:bg-slate-800/80 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
            Контроллер
          </span>
          {ruleResult.assessment_status && (
            <span className="inline-flex shrink-0 rounded-full border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300">
              {ruleResult.assessment_status}
            </span>
          )}
        </>
      }
      title={serial}
      scoreDisplay={formatScore(score)}
      riskLevel={riskLevel}
      scoreBarValue={numericScore ?? 0}
      feature={<Risk4ForecastFeature projection={envelope} />}
      metrics={<RiskHitMetricGrid rows={criticalRows} />}
      details={
        <>
          <RiskHitMetricGrid rows={detailRows} />
          <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
            Risk #4 v2 — индекс свидетельств и приоритет диагностики питания, не вероятность смерти
            батареи. Качество оценки и качество данных показываются отдельно. Поле «До отказа» (RUL)
            без калиброванной модели не вычисляется.
          </p>
          {ruleResult.reason_codes?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs text-slate-500">Причины решения</p>
              <div className="flex flex-wrap gap-1.5">
                {ruleResult.reason_codes.map((code) => (
                  <span
                    key={code}
                    className="rounded-full bg-white px-2 py-1 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400"
                  >
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      }
      actions={
        deviceId != null ? (
          <>
            {onOpenHistory && (
              <button
                type="button"
                onClick={() => onOpenHistory({ ...facts, risk_number: 4 })}
                className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionHistory}`}
              >
                История рисков
              </button>
            )}
            <Link
              to={buildDeviceHistoryPath(deviceId, { serialNumber: serial })}
              className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionSecondary}`}
            >
              График напряжения
            </Link>
          </>
        ) : null
      }
    />
  );
}
